/**
 * Calculateur de risques agricoles universels
 * Basé sur données météo + paramètres culture
 *
 * Risques implémentés :
 * 1. Stress VPD (Vapor Pressure Deficit) - Universel
 * 2. Stress thermique (froid/chaleur) - Universel
 * 3. Oïdium (Powdery Mildew) - Majorité cultures
 */
class AgriculturalRiskCalculator {

  /**
   * Seuils de risques par culture
   * Structure : THRESHOLDS[culture][risk] = { seuils }
   */
  static THRESHOLDS = {
    framboises: {
      vpdStress: {
        optimal_min: 0.8,
        optimal_max: 1.2,
        critical_low: 0.4,
        critical_high: 2.0
      },
      thermalStress: {
        cold_warning: 0,
        cold_critical: -2,
        heat_warning: 28,
        heat_critical: 32
      },
      powderyMildew: {
        temp_optimal: { min: 18, max: 28, peak_min: 20, peak_max: 25 },
        humidity_optimal: { min: 40, max: 70 },
        vpd_favorable: { min: 0.6, max: 1.4 }
      }
    },
    tomates: {
      vpdStress: {
        optimal_min: 0.8,
        optimal_max: 1.4,
        critical_low: 0.5,
        critical_high: 2.5
      },
      thermalStress: {
        cold_warning: 5,
        cold_critical: 2,
        heat_warning: 32,
        heat_critical: 35
      },
      powderyMildew: {
        temp_optimal: { min: 20, max: 30, peak_min: 24, peak_max: 28 },
        humidity_optimal: { min: 50, max: 80 },
        vpd_favorable: { min: 0.8, max: 1.6 }
      }
    },
    generic: {
      vpdStress: {
        optimal_min: 0.8,
        optimal_max: 1.2,
        critical_low: 0.4,
        critical_high: 2.0
      },
      thermalStress: {
        cold_warning: 5,
        cold_critical: 0,
        heat_warning: 30,
        heat_critical: 35
      },
      powderyMildew: {
        temp_optimal: { min: 20, max: 30, peak_min: 23, peak_max: 27 },
        humidity_optimal: { min: 40, max: 70 },
        vpd_favorable: { min: 0.8, max: 1.2 }
      }
    }
  };

  /**
   * Calcule le VPD (Vapor Pressure Deficit) en kPa
   * Formule de Magnus-Tetens
   * @param {number} temperature - Température en °C
   * @param {number} humidity - Humidité relative en %
   * @returns {number} VPD en kPa
   */
  static calculateVPD(temperature, humidity) {
    const saturationVP = 0.6108 * Math.exp((17.27 * temperature) / (temperature + 237.3));
    const actualVP = saturationVP * (humidity / 100);
    return saturationVP - actualVP;
  }

  /**
   * Risque 1 : Stress VPD (0-100)
   * VPD trop bas (<0.4) = maladies fongiques
   * VPD trop haut (>2.0) = déshydratation/stress hydrique
   * VPD optimal (0.8-1.2) = 0 stress
   *
   * @param {number} temperature - Température en °C
   * @param {number} humidity - Humidité relative en %
   * @param {string} culture - Type de culture (framboises, tomates, generic)
   * @returns {number} Niveau de stress VPD (0-100)
   */
  static calculateVPDStress(temperature, humidity, culture = 'generic') {
    const thresholds = this.THRESHOLDS[culture]?.vpdStress || this.THRESHOLDS.generic.vpdStress;
    const vpd = this.calculateVPD(temperature, humidity);

    let stress = 0;

    // VPD optimal = 0 stress
    if (vpd >= thresholds.optimal_min && vpd <= thresholds.optimal_max) {
      stress = 0;
    }
    // VPD trop bas (< optimal_min) = risque fongique
    else if (vpd < thresholds.optimal_min) {
      if (vpd < thresholds.critical_low) {
        stress = 100; // Critique
      } else {
        // Interpolation linéaire
        stress = Math.round(((thresholds.optimal_min - vpd) / (thresholds.optimal_min - thresholds.critical_low)) * 100);
      }
    }
    // VPD trop haut (> optimal_max) = stress hydrique
    else if (vpd > thresholds.optimal_max) {
      if (vpd > thresholds.critical_high) {
        stress = 100; // Critique
      } else {
        // Interpolation linéaire
        stress = Math.round(((vpd - thresholds.optimal_max) / (thresholds.critical_high - thresholds.optimal_max)) * 100);
      }
    }

    return Math.min(100, Math.max(0, stress));
  }

  /**
   * Risque 2 : Stress thermique (0-100)
   * Mesure l'exposition au froid ou à la chaleur excessive
   *
   * @param {number} temperature - Température en °C
   * @param {string} culture - Type de culture (framboises, tomates, generic)
   * @returns {number} Niveau de stress thermique (0-100)
   */
  static calculateThermalStress(temperature, culture = 'generic') {
    const thresholds = this.THRESHOLDS[culture]?.thermalStress || this.THRESHOLDS.generic.thermalStress;

    let stress = 0;

    // Stress froid
    if (temperature < thresholds.cold_warning) {
      if (temperature <= thresholds.cold_critical) {
        stress = 100; // Gel critique
      } else {
        // Interpolation linéaire
        stress = Math.round(((thresholds.cold_warning - temperature) / (thresholds.cold_warning - thresholds.cold_critical)) * 100);
      }
    }
    // Stress chaleur
    else if (temperature > thresholds.heat_warning) {
      if (temperature >= thresholds.heat_critical) {
        stress = 100; // Chaleur critique
      } else {
        // Interpolation linéaire
        stress = Math.round(((temperature - thresholds.heat_warning) / (thresholds.heat_critical - thresholds.heat_warning)) * 100);
      }
    }

    return Math.min(100, Math.max(0, stress));
  }

  /**
   * Risque 3 : Oïdium / Powdery Mildew (0-100)
   * Conditions favorables : T° modérée + HR modérée + VPD dans plage
   *
   * Score :
   * - Température optimale : 50 points max
   * - Humidité optimale : 40 points max
   * - VPD favorable : 10 points bonus
   *
   * @param {number} temperature - Température en °C
   * @param {number} humidity - Humidité relative en %
   * @param {string} culture - Type de culture (framboises, tomates, generic)
   * @returns {number} Niveau de risque Oïdium (0-100)
   */
  static calculatePowderyMildewRisk(temperature, humidity, culture = 'generic') {
    const thresholds = this.THRESHOLDS[culture]?.powderyMildew || this.THRESHOLDS.generic.powderyMildew;
    const vpd = this.calculateVPD(temperature, humidity);

    let risk = 0;

    // Score température (max 50 points)
    if (temperature >= thresholds.temp_optimal.min && temperature <= thresholds.temp_optimal.max) {
      risk += 40;
      if (temperature >= thresholds.temp_optimal.peak_min && temperature <= thresholds.temp_optimal.peak_max) {
        risk += 10; // Pic = 50 points total
      }
    } else if (temperature >= thresholds.temp_optimal.min - 5 && temperature <= thresholds.temp_optimal.max + 5) {
      risk += 20; // Plage acceptable = 20 points
    }

    // Score humidité (max 40 points)
    if (humidity >= thresholds.humidity_optimal.min && humidity <= thresholds.humidity_optimal.max) {
      risk += 40;
    } else if (humidity >= thresholds.humidity_optimal.min - 10 && humidity <= thresholds.humidity_optimal.max + 10) {
      risk += 20;
    }

    // Bonus VPD favorable (max 10 points)
    if (vpd >= thresholds.vpd_favorable.min && vpd <= thresholds.vpd_favorable.max) {
      risk += 10;
    }

    return Math.min(100, Math.round(risk));
  }

  /**
   * Fonction principale : calcule les 3 risques agricoles
   *
   * @param {number} temperature - Température en °C
   * @param {number} humidity - Humidité relative en %
   * @param {string} culture - Type de culture (framboises, tomates, generic)
   * @returns {Object} { vpdStress, thermalStress, powderyMildewRisk, vpd }
   */
  static calculateAllRisks(temperature, humidity, culture = 'generic') {
    // Normaliser culture (fallback à generic si non reconnue)
    const normalizedCulture = this.THRESHOLDS[culture] ? culture : 'generic';

    return {
      vpdStress: this.calculateVPDStress(temperature, humidity, normalizedCulture),
      thermalStress: this.calculateThermalStress(temperature, normalizedCulture),
      powderyMildewRisk: this.calculatePowderyMildewRisk(temperature, humidity, normalizedCulture),
      vpd: Math.round(this.calculateVPD(temperature, humidity) * 100) / 100 // Pour affichage
    };
  }

  /**
   * Obtient le libellé de niveau de risque
   * @param {number} riskValue - Valeur du risque (0-100)
   * @returns {string} Libellé du niveau
   */
  static getRiskLevel(riskValue) {
    if (riskValue === 0) return 'Aucun';
    if (riskValue < 25) return 'Faible';
    if (riskValue < 50) return 'Modéré';
    if (riskValue < 75) return 'Élevé';
    return 'Critique';
  }

  /**
   * Obtient la couleur associée au niveau de risque
   * @param {number} riskValue - Valeur du risque (0-100)
   * @returns {string} Nom de couleur Tailwind
   */
  static getRiskColor(riskValue) {
    if (riskValue === 0) return 'green';
    if (riskValue < 25) return 'blue';
    if (riskValue < 50) return 'yellow';
    if (riskValue < 75) return 'orange';
    return 'red';
  }
}

export default AgriculturalRiskCalculator;
